import SectionCard from "@/components/Card/SectionCard";
import type { IconName } from "@/components/Icon/iconMap";
import { HStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { type PropsWithChildren, useCallback, useMemo } from "react";
import { useCFormStore } from "../cform-store";
import type { EditButtonGroupProps } from "../cform-types";
import { CFormSectionEditButtonGroup } from "./CFormSectionEditButtonGroup";
import { CFormSectionStore } from "./cform-section-store";

type Props = PropsWithChildren<{
  /**
   * NavigationSection との弱い互換性
   */

  /** hash scroll の id としても活用される */
  id: string;
  /** 表示名 */
  label: string;
  /** アイコン */
  icon?: IconName;
  /** 編集ボタングループコンポーネント */
  editButtonGroupComponent?: React.ComponentType<EditButtonGroupProps>;

  /**
   * <CForm> 内で活用し、以下の項目群を明示的に指定しない場合、自動的に注入されます。
   */

  /** アクションボタンを非表示にする (アイテム単位での編集の場合指定) */
  hideActionButtons?: boolean;
  /** 編集中 */
  isEditing?: boolean;
  /** 編集中 */
  isPending?: boolean;
  /** 編集開始 */
  onStartEdit?: () => void;
  /** 編集キャンセル */
  onCancel?: () => void;
  /** 保存 */
  onSave?: () => void;
}>;

/**
 * Card-based Form / Section
 * 各アイテムの親のコンテナ部分
 */
export function CFormSection(props: Props) {
  const store = CFormSectionStore.createStore({
    state: { sectionId: props.id },
  });

  return (
    <CFormSectionStore.Provider value={store}>
      <CFormSectionContent {...props} />
    </CFormSectionStore.Provider>
  );
}

export function CFormSectionContent(props: Props) {
  const cFormStore = useCFormStore((store) => store, { allowNoContext: true });

  // props + cFormStore のマージ
  const mergedProps = useMemo(() => {
    return {
      hideActionButtons:
        props.hideActionButtons ??
        cFormStore?.state.isHideSectionActions({ sectionId: props.id }),
      isEditing:
        props.isEditing ??
        cFormStore?.state.isSectionEditing({ sectionId: props.id }),
      isPending: props.isPending ?? cFormStore?.state.isPending,
      onStartEdit:
        props.onStartEdit ??
        (() => cFormStore?.actions.startEditSection(props.id)),
      onCancel: props.onCancel ?? cFormStore?.actions.reset,
      onSave: props.onSave ?? cFormStore?.actions.save,
    };
  }, [props, cFormStore?.state, cFormStore?.actions]);

  const scrollFocus = useCallback(() => {
    window.location.hash = props.id;
  }, [props.id]);

  const onStartEdit = useCallback(() => {
    mergedProps.onStartEdit?.();
    scrollFocus();
  }, [mergedProps.onStartEdit, scrollFocus]);

  const onCancel = useCallback(() => {
    mergedProps.onCancel?.();
    scrollFocus();
  }, [mergedProps.onCancel, scrollFocus]);

  const onSave = useCallback(() => {
    mergedProps.onSave?.();
    scrollFocus();
  }, [mergedProps.onSave, scrollFocus]);

  const EditButtonGroup =
    props.editButtonGroupComponent ?? CFormSectionEditButtonGroup;

  return (
    <SectionCard
      key={props.id}
      icon={props.icon}
      id={props.id}
      title={
        <HStack className="gap-2 items-center w-full justify-between">
          <Text variant="h4">{props.label}</Text>
          <EditButtonGroup
            display={!mergedProps.hideActionButtons}
            isEditing={mergedProps.isEditing}
            onStartEdit={onStartEdit}
            onCancel={onCancel}
            onSave={onSave}
            isPending={mergedProps.isPending}
          />
        </HStack>
      }
    >
      {props.children}
    </SectionCard>
  );
}
